import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { EggmanLogo } from "./EggmanLogo";
import NotificationBell from "./NotificationBell";
import { LogOut, Home } from "lucide-react";

export default function DashboardShell({ navItems, accentLabel }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = accentLabel ? accentLabel.toLowerCase() : "customer";

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col md:flex-row" data-testid="dashboard-shell">
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-stone-200 bg-white flex md:flex-col">
        <div className="p-6 border-b border-stone-200 hidden md:block">
          <EggmanLogo />
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-[#166534] text-[10px] uppercase tracking-widest font-bold">
            {accentLabel}
          </div>
        </div>

        <nav className="p-3 md:p-4 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible flex-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-[#166534] text-white shadow-sm"
                    : item.highlight
                      ? "bg-orange-50 text-[#EA580C] hover:bg-orange-100"
                      : "text-stone-600 hover:bg-stone-100"
                }`
              }
            >
              {item.icon ? <item.icon size={16} /> : null}
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block p-4 border-t border-stone-200">
          <div className="text-xs text-stone-500 mb-2">Signed in as</div>
          <div className="text-sm font-semibold text-stone-900 truncate">{user?.name}</div>
          <div className="text-xs text-stone-500 truncate">{user?.email}</div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => navigate("/")}
              data-testid="dashboard-home-btn"
              className="flex-1 flex items-center justify-center gap-2 text-xs font-semibold py-2 rounded-lg border border-stone-200 hover:bg-stone-50"
            >
              <Home size={14} /> Site
            </button>
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
              data-testid="dashboard-logout-btn"
              className="flex-1 flex items-center justify-center gap-2 text-xs font-semibold py-2 rounded-lg bg-stone-900 text-white hover:bg-black"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="hidden md:flex items-center justify-end gap-2 px-10 py-3 border-b border-stone-200 bg-white">
          <NotificationBell role={role} testid="notification-bell-desktop" />
        </div>
        <div className="md:hidden flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-white">
          <EggmanLogo />
          <div className="flex items-center gap-1">
            <NotificationBell role={role} testid="notification-bell-mobile" />
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
              data-testid="dashboard-logout-btn-mobile"
              className="text-xs font-semibold py-2 px-3 rounded-lg bg-stone-900 text-white"
            >
              Logout
            </button>
          </div>
        </div>
        <main className="px-5 py-7 md:px-10 md:py-10 max-w-6xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
